import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';

import { ImageAssetWithAltText } from '@fhs/ui';

import type { Step } from '../components/guide/types';
import { useMenuGuideQuery } from '../queries/use-menu-guide-query';
import { useMenuQueryOpts } from '../queries/use-menu-query-opts';
import type { CustomizationSelections, MenuPickerData } from '../types';

import { useMenuPicker } from './menu-picker';

export type SelectedPicker = {
  id: string;
  slug: string;
  // Derived
  displayName: string;
  priceCents: number;

  // Instructions
  instructions?: string;

  // Picker
  pickerSelections?: Record<string, string>;

  // PickerAspectOption
  selectedPickerAspectOptionId?: string;
  selectedPickerAspectOption?:
    | null
    | NonNullable<MenuPickerData['pickerAspect']>['options'][number];

  // Customizations
  defaultCustomizationSelections?: CustomizationSelections;
  userCustomizationSelections?: CustomizationSelections;
  resolvedCustomizationSelections?: CustomizationSelections;

  draftCustomizationSelections?: CustomizationSelections;
  requiresCustomization: boolean;

  // UI
  image: ImageAssetWithAltText;
};

export type ComboSelectionContext = {
  isComboEnabled: boolean;
  selectedPickers: Record<string, SelectedPicker>;
  isEditingPickers: boolean;
  setIsComboEnabled: (enabled: boolean) => void;
  setSelectedPickers: (pickers: Record<string, SelectedPicker>) => void;
  addOrUpdateSelectedPicker: (step: string, picker: SelectedPicker) => void;
  setIsEditingPickers: (editing: boolean) => void;
  isComboGuideOpen: boolean;
  setIsComboGuideOpen: (value: boolean) => void;
  shouldAddToCartDirectly: boolean;
  setShouldAddToCartDirectly: (value: boolean) => void;
  guideSteps: Array<Step>;
};

type ComboSelectionProviderStateProps = {
  children?: ReactNode;
  guideSteps: Array<Step>;
  initialState?: {
    isComboEnabled?: boolean;
    comboSelectedPickers?: Record<string, SelectedPicker>;
  };
};

const comboSelectionContext = createContext<ComboSelectionContext | null>(null);

const ComboSelectionProviderState = ({
  children,
  initialState,
  guideSteps,
}: ComboSelectionProviderStateProps) => {
  const [isComboEnabled, setIsComboEnabled] = useState(initialState?.isComboEnabled ?? false);
  const [selectedPickers, setSelectedPickers] = useState<Record<string, SelectedPicker>>(
    initialState?.comboSelectedPickers ?? {}
  );
  const [isEditingPickers, setIsEditingPickers] = useState(false);
  const [isComboGuideOpen, setIsComboGuideOpen] = useState(false);
  const [shouldAddToCartDirectly, setShouldAddToCartDirectly] = useState(false);

  const addOrUpdateSelectedPicker = useCallback(
    (step: string, picker: SelectedPicker) => {
      setSelectedPickers(prevPickers => ({
        ...prevPickers,
        [step]: picker, // Add or replace the picker for the given step
      }));
    },
    [setSelectedPickers]
  );

  const value: ComboSelectionContext = {
    selectedPickers,
    setSelectedPickers,
    addOrUpdateSelectedPicker,
    isEditingPickers,
    setIsEditingPickers,
    isComboEnabled,
    setIsComboEnabled,
    isComboGuideOpen,
    setIsComboGuideOpen,
    shouldAddToCartDirectly,
    setShouldAddToCartDirectly,
    guideSteps,
  };

  return <comboSelectionContext.Provider value={value}>{children}</comboSelectionContext.Provider>;
};

type ComboSelectionProviderProps = {
  children: ReactNode;
  initialState?: {
    isComboEnabled?: boolean;
    comboSelectedPickers?: Record<string, SelectedPicker>;
  };
};
export const ComboSelectionProvider = ({ initialState, children }: ComboSelectionProviderProps) => {
  const { query: menuPickerQuery } = useMenuPicker();
  const comboId = menuPickerQuery.data?.combo?.id ?? '';

  const menuGuideQuery = useMenuGuideQuery({
    enabled: Boolean(comboId),
    comboId,
    ...useMenuQueryOpts(),
  });

  return (
    <ComboSelectionProviderState
      initialState={initialState}
      guideSteps={menuGuideQuery.data?.steps ?? []}
      // Reset combo selection provider state when combo id changes or initialState updates
      key={[comboId, initialState && JSON.stringify(initialState)].filter(Boolean).join('-')}
    >
      {children}
    </ComboSelectionProviderState>
  );
};

export const useComboSelectionContext = (): ComboSelectionContext => {
  const context = useContext(comboSelectionContext);

  if (!context) {
    throw new Error(
      'useComboSelectionContext can only be called from within a descendant of ComboSelectionProvider'
    );
  }

  return context;
};
